/**
 * Подготовка фото в браузере — один вход для всех мест загрузки: слоты виджета и тренда, последний кадр,
 * свап, референсы Omni, этапы «фото → видео», карточка товара, мастерская. Правила — imageInput.ts
 * (владелец 29.09): подходящее уходит байт в байт, чинится только то, без чего провайдер откажет,
 * ничего не растягивается.
 */
import { requestImageCrop } from '../../../utils/imageCropRequest'
import type { ImageLimits } from './referenceLimits'
import {
	PASS_FORMATS,
	canonicalName,
	capPixels,
	describeFix,
	planImage,
	sniffImageFormat,
	type CropRect,
	type ImageFormat,
} from './imageInput'

export type PrepareReject = 'format' | 'too_big' | 'dimensions'

export type PreparedImage =
	| { ok: true; file: File; note?: string }
	| { ok: false; cancelled?: false; reason: PrepareReject; message: string }
	/** Клиент закрыл окно выбора рамки — молча ничего не грузим. */
	| { ok: false; cancelled: true }

const MB = 1024 * 1024

/** Форматы, которые переведёт в JPEG сервер при загрузке, если не умеет браузер (HEIC в Chrome и т. п.). */
const SERVER_CONVERTS: readonly ImageFormat[] = ['heic', 'avif', 'tiff', 'bmp']

export function tooHeavyMessage(maxMb: number): string {
	return `Файл слишком большой: максимум ${maxMb} МБ. Сохраните фото в меньшем размере и загрузите снова.`
}

async function sniff(file: File): Promise<ImageFormat> {
	try {
		return sniffImageFormat(new Uint8Array(await file.slice(0, 32).arrayBuffer()))
	} catch {
		return 'unknown'
	}
}

/** Страховка открытия: не дождались — фото уходит как есть (как до подгонки), загрузка не висит. */
const OPEN_TIMEOUT_MS = 15_000

/**
 * Открыть картинку браузером (поворот из EXIF браузер применяет сам). Не умеет (HEIC в Chrome) — null.
 * Ждём load, а не img.decode(): decode() в скрытой вкладке Chrome не завершается, пока её не откроют (замер
 * 29.09: фото 900×1200 висело «загрузкой» в фоновой вкладке), а размерам и отрисовке на холсте хватает load.
 */
async function openImage(file: File): Promise<{ img: HTMLImageElement; url: string } | null> {
	const url = URL.createObjectURL(file)
	const img = new Image()
	const loaded = await new Promise<boolean>((resolve) => {
		const timer = window.setTimeout(() => resolve(false), OPEN_TIMEOUT_MS)
		img.onload = () => {
			window.clearTimeout(timer)
			resolve(true)
		}
		img.onerror = () => {
			window.clearTimeout(timer)
			resolve(false)
		}
		img.src = url
	})
	img.onload = null
	img.onerror = null
	if (loaded && img.naturalWidth && img.naturalHeight) return { img, url }
	URL.revokeObjectURL(url)
	return null
}

function draw(img: HTMLImageElement, crop: CropRect | null, width: number, height: number, type: string, quality?: number): Promise<Blob | null> {
	const canvas = document.createElement('canvas')
	canvas.width = width
	canvas.height = height
	const ctx = canvas.getContext('2d')
	if (!ctx) return Promise.resolve(null)
	ctx.imageSmoothingEnabled = true
	ctx.imageSmoothingQuality = 'high'
	if (type === 'image/jpeg') {
		// Прозрачное (PNG, GIF) в JPEG — на белом фоне, а не на чёрном.
		ctx.fillStyle = '#fff'
		ctx.fillRect(0, 0, width, height)
	}
	const c = crop ?? { x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight }
	ctx.drawImage(img, c.x, c.y, c.w, c.h, 0, 0, width, height)
	return new Promise((resolve) =>
		canvas.toBlob(
			(b) => {
				// Память холста — сразу назад: Safari на iPhone держит её до сборки мусора, а попыток сжатия бывает несколько.
				canvas.width = 0
				canvas.height = 0
				resolve(b)
			},
			type,
			quality,
		),
	)
}

function withExt(name: string, ext: string): string {
	const dot = name.lastIndexOf('.')
	return `${dot > 0 ? name.slice(0, dot) : name || 'photo'}.${ext}`
}

/**
 * Подготовить фото к загрузке по рамкам модели (null — у модели своих рамок нет: только упаковка и вес).
 * maxMb — потолок веса файла на сайте.
 */
export async function prepareImageInput(file: File, rules: ImageLimits | null | undefined, opts: { maxMb: number }): Promise<PreparedImage> {
	const maxBytes = opts.maxMb * MB
	const format = await sniff(file)
	const decoded = await openImage(file)

	if (!decoded) {
		// Браузер не открыл. HEIC, AVIF, TIFF и BMP переведёт в JPEG сервер; обычный JPEG/PNG/WebP/GIF бывает не
		// открыть от нехватки памяти (огромное фото на телефоне) — такой уходит как есть, как было до подгонки.
		// Размеры тут не проверить: снимки с телефона (3024×4032) под рамки и так проходят.
		if (SERVER_CONVERTS.includes(format) || PASS_FORMATS.includes(format) || format === 'gif') {
			if (file.size > maxBytes) return { ok: false, reason: 'too_big', message: tooHeavyMessage(opts.maxMb) }
			const canon = canonicalName(file.name, file.type, format)
			return { ok: true, file: canon ? new File([file], canon.name, { type: canon.type, lastModified: file.lastModified }) : file }
		}
		return { ok: false, reason: 'format', message: 'Файл не похож на фото. Загрузите jpg, png, webp или HEIC.' }
	}

	const { img, url } = decoded
	try {
		const w = img.naturalWidth
		const h = img.naturalHeight
		let plan = planImage(w, h, rules)
		if (plan.kind === 'ask_crop') {
			const userCrop = await requestImageCrop({ url, width: w, height: h, ratio: plan.ratio })
			if (!userCrop) return { ok: false, cancelled: true }
			plan = planImage(w, h, rules, userCrop)
		}
		if (plan.kind === 'reject') return { ok: false, reason: 'dimensions', message: plan.message }
		// После выбора рамки план не спрашивает снова: рамка уже нужной пропорции.
		if (plan.kind === 'ask_crop') return { ok: false, cancelled: true }

		if (plan.kind === 'keep' && PASS_FORMATS.includes(format) && file.size <= maxBytes) {
			// Подходит — не трогаем: те же байты, разве что имя по настоящему формату (.jfif → .jpg).
			const canon = canonicalName(file.name, file.type, format)
			return { ok: true, file: canon ? new File([file], canon.name, { type: canon.type, lastModified: file.lastModified }) : file }
		}

		const crop = plan.kind === 'fix' ? plan.crop : null
		const fromW = crop ? crop.w : w
		const fromH = crop ? crop.h : h
		let { width, height } = capPixels(plan.kind === 'fix' ? plan.width : fromW, plan.kind === 'fix' ? plan.height : fromH)
		// Прозрачность возможна у PNG, GIF, WebP — оставляем PNG (без потерь); остальное — JPEG 95 %.
		let type = format === 'png' || format === 'gif' || format === 'webp' ? 'image/png' : 'image/jpeg'
		let blob = await draw(img, crop, width, height, type, type === 'image/jpeg' ? 0.95 : undefined)
		// Тяжелее потолка сайта: сначала JPEG, потом по шагу меньше, но не ниже минимума модели.
		for (let i = 0; blob && blob.size > maxBytes && i < 6; i++) {
			if (type === 'image/png') {
				type = 'image/jpeg'
			} else {
				const nw = Math.round(width * 0.85)
				const nh = Math.round(height * 0.85)
				if (rules && Math.min(nw, nh) < rules.minPx) break
				width = nw
				height = nh
			}
			blob = await draw(img, crop, width, height, type, 0.92)
		}
		if (!blob) {
			// Холст не отдал картинку (не хватило памяти телефона) — отправляем как есть.
			return file.size <= maxBytes ? { ok: true, file } : { ok: false, reason: 'too_big', message: tooHeavyMessage(opts.maxMb) }
		}
		if (blob.size > maxBytes) return { ok: false, reason: 'too_big', message: tooHeavyMessage(opts.maxMb) }
		const out = new File([blob], withExt(file.name, type === 'image/png' ? 'png' : 'jpg'), { type, lastModified: Date.now() })
		const note = describeFix({
			cropped: !!crop,
			fromW,
			fromH,
			toW: width,
			toH: height,
			weightMb: file.size > maxBytes ? out.size / MB : undefined,
		})
		return { ok: true, file: out, note }
	} finally {
		URL.revokeObjectURL(url)
	}
}
